import { useState, useCallback, useEffect, useRef } from "react";
import { listFoldersByTenant } from "../../../../../../services/folders";
import { listDocumentsByTenant } from "../../../../../../services/document";

const getFileType = (fileName) => {
  if (!fileName) return "default";
  const extension = fileName.split(".").pop()?.toLowerCase();
  return extension || "default";
};

const DRIVE_PREFIX = "drive:";
const FOLDER_PREFIX = "folder:";

/**
 * Hook para el árbol del DrivePicker: muestra drives en raíz y carga carpetas/documentos por drive.
 * Mantiene treeData en memoria al cerrar el modal para no refetch al reabrir.
 * @param {Array} drives - Lista de drives { id, name }
 * @param {boolean} isOpen - Si el modal está abierto
 * @returns {Object} treeData, loadingFolders, loadingDrives, isInitialLoading, loadDriveContent, loadFolderChildren
 */
export const useDrivePickerTree = (drives, isOpen) => {
  const [treeData, setTreeData] = useState({});
  const [loadingFolders, setLoadingFolders] = useState({});
  const [loadingDrives, setLoadingDrives] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const loadingRef = useRef({});
  const loadedRef = useRef({});

  const loadDriveContent = useCallback(async (vaultUuid) => {
    if (!vaultUuid) return;
    const key = `${DRIVE_PREFIX}${vaultUuid}`;
    if (loadedRef.current[key]) return;
    if (loadingRef.current[key]) return;

    loadingRef.current[key] = true;
    setLoadingDrives((prev) => ({ ...prev, [vaultUuid]: true }));

    try {
      const [foldersResponse, documentsResponse] = await Promise.all([
        listFoldersByTenant({
          vault_uuid: vaultUuid,
          parent_folder_uuid: null,
          page: 1,
          page_size: 100,
          order_by: ["updated_at"],
          order_direction: ["DESC"],
          filters: {},
          cursor: null,
        }),
        listDocumentsByTenant({
          vault_uuid: vaultUuid,
          page: 1,
          page_size: 100,
        }),
      ]);

      const folders = foldersResponse?.data?.body || [];
      const documents = documentsResponse?.data?.body || [];

      const rootFolders = folders
        .filter((folder) => {
          const folderId =
            folder.folder_uuid || folder.folderUuid || folder.id || folder.uuid;
          const parentId =
            folder.parent_folder_uuid || folder.parentFolderUuid;
          return parentId !== folderId;
        })
        .map((folder) => {
          const folderId =
            folder.folder_uuid || folder.folderUuid || folder.id || folder.uuid;
          return {
            id: folderId,
            name: folder.name,
            type: "folder",
            folder_uuid: folderId,
            vault_uuid: vaultUuid,
          };
        });

      const rootDocuments = documents.map((doc) => {
        const docId =
          doc.document_uuid || doc.documentUuid || doc.id || doc.uuid;
        return {
          id: docId,
          name: doc.name,
          type: getFileType(doc.name),
          document_uuid: docId,
        };
      });

      setTreeData((prev) => ({
        ...prev,
        [key]: { folders: rootFolders, documents: rootDocuments },
      }));

      loadedRef.current[key] = true;
    } catch (error) {
      // Caller can handle errors
    } finally {
      delete loadingRef.current[key];
      setLoadingDrives((prev) => {
        const { [vaultUuid]: removed, ...rest } = prev;
        return rest;
      });
    }
  }, []);

  const loadFolderChildren = useCallback(
    async (folderUuid, vaultUuid) => {
      if (!vaultUuid || !folderUuid) return;
      const key = `${FOLDER_PREFIX}${folderUuid}`;
      if (loadedRef.current[key]) return;
      if (loadingRef.current[key]) return;

      loadingRef.current[key] = true;
      setLoadingFolders((prev) => ({ ...prev, [folderUuid]: true }));

      try {
        const [foldersResponse, documentsResponse] = await Promise.all([
          listFoldersByTenant({
            parent_folder_uuid: folderUuid,
            vault_uuid: vaultUuid,
            page: 1,
            page_size: 100,
            order_by: ["updated_at"],
            order_direction: ["DESC"],
            filters: {},
            cursor: null,
          }),
          listDocumentsByTenant({
            vault_uuid: vaultUuid,
            folder_uuid: folderUuid,
            page: 1,
            page_size: 100,
          }),
        ]);

        const folders = foldersResponse?.data?.body || [];
        const documents = documentsResponse?.data?.body || [];

        const childFolders = folders
          .filter((folder) => {
            const folderId =
              folder.folder_uuid || folder.folderUuid || folder.id || folder.uuid;
            const parentId =
              folder.parent_folder_uuid || folder.parentFolderUuid;
            if (folderId === folderUuid) return false;
            if (parentId === folderId) return false;
            return true;
          })
          .map((folder) => {
            const folderId =
              folder.folder_uuid || folder.folderUuid || folder.id || folder.uuid;
            return {
              id: folderId,
              name: folder.name,
              type: "folder",
              folder_uuid: folderId,
              vault_uuid: vaultUuid,
            };
          });

        const childDocuments = documents.map((doc) => {
          const docId =
            doc.document_uuid || doc.documentUuid || doc.id || doc.uuid;
          return {
            id: docId,
            name: doc.name,
            type: getFileType(doc.name),
            document_uuid: docId,
          };
        });

        setTreeData((prev) => ({
          ...prev,
          [key]: { folders: childFolders, documents: childDocuments },
        }));

        loadedRef.current[key] = true;
      } catch (error) {
        // Caller can handle errors
      } finally {
        delete loadingRef.current[key];
        setLoadingFolders((prev) => {
          const { [folderUuid]: removed, ...rest } = prev;
          return rest;
        });
      }
    },
    []
  );

  useEffect(() => {
    if (isOpen && drives?.length > 0) {
      setIsInitialLoading(false);
    } else if (isOpen && (!drives || drives.length === 0)) {
      setIsInitialLoading(true);
    }
  }, [isOpen, drives]);

  return {
    treeData,
    loadingFolders,
    loadingDrives,
    isInitialLoading,
    loadDriveContent,
    loadFolderChildren,
    DRIVE_PREFIX,
    FOLDER_PREFIX,
  };
};
